// World import (M51.6, EDU-2). The inverse of `storage/export.ts`: takes `.tfworld`
// JSONL text and writes it into durable storage as a new (or overwritten) world —
// used both by a plain "import a .tfworld file" flow and by the create screen's
// Lesson tab (§29.3), which imports a lesson pack's inline template.
import type { WorldFileStore, WorldMeta } from './saves';
import { CURRENT_SAVE_VERSION } from './migrate';
import { parseWorldFile, type WorldFile } from '../../sim/save/worldfile';
import type { BlockEntityType } from '../../sim/game/blockentity';
import type { SavedEntity } from '../../sim/entities/entity';

/** A `.tfworld` file failed to parse — truncated, not JSONL, or a bad line. Thrown
 *  BEFORE any write, so a rejected import never leaves a partial world (M51.6
 *  Verify). A distinct type (rather than a bare `Error`) so a caller — the Lesson
 *  tab, a plain "import world" menu action — can distinguish "this file is bad"
 *  from any other failure and word its own message accordingly. */
export class WorldFileImportError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'WorldFileImportError';
  }
}

export interface ImportOptions {
  /** World id to assign; default the file's own `meta.id`. A lesson creating a
   *  new world always passes a fresh one — reusing the source id would silently
   *  overwrite an existing save of the same name. */
  id?: string;
  name?: string;
  /** Applied over the file's own meta (a lesson merges its gamerules over the
   *  create-form's, §29.3) — set directly on the written `WorldMeta`. */
  gamerules?: Record<string, string>;
}

export interface ImportResult {
  meta: WorldMeta;
  sections: number;
  blockEntities: number;
  mobs: number;
}

const HOST_DIM = 'overworld'; // the one dimension a `.tfworld` file carries (export.ts)

/** Parse `text` and write it into `save` as a new world: meta, edited sections,
 *  and block entities (chest contents, a spawner's config, …) — no player row
 *  (a freshly imported world has none yet; the caller spawns one normally, the
 *  same as any other brand-new world). */
export async function importWorldFile(save: WorldFileStore, text: string, opts: ImportOptions = {}): Promise<ImportResult> {
  let file: WorldFile;
  try {
    file = parseWorldFile(text);
  } catch (e) {
    throw new WorldFileImportError((e as Error).message);
  }

  const now = Date.now();
  const meta: WorldMeta = {
    id: opts.id ?? file.meta.id,
    name: opts.name ?? file.meta.name,
    seed: file.meta.seed,
    worldType: file.meta.worldType,
    gameMode: file.meta.gameMode,
    difficulty: file.meta.difficulty,
    hardcore: file.meta.hardcore,
    allowCommands: file.meta.gameMode !== 'survival',
    createdAt: now,
    playedAt: now,
    version: CURRENT_SAVE_VERSION,
  };
  if (file.meta.flatPreset) meta.flatPreset = file.meta.flatPreset;
  if (opts.gamerules) meta.gamerules = opts.gamerules;

  await save.putWorld(meta);
  const dirty = file.sections.map((s) => ({ cx: s.cx, cz: s.cz, sy: s.sy, states: s.states }));
  await save.putSections(meta.id, HOST_DIM, dirty);
  // `.tfworld` block-entity types are hand-validated like the rest of the format
  // (worldfile.ts never pins them to the closed `BlockEntityType` set, same as it
  // never pins a section's `dim`) — this cast carries the same trust level as the
  // rest of a parsed worldfile, not a new gap this task introduces.
  const beRows = file.blockEntities.map((be) => ({ type: be.type as BlockEntityType, x: be.x, y: be.y, z: be.z, data: be.data }));
  await save.saveBlockEntities(meta.id, HOST_DIM, beRows);

  // M53.5 (D-TF-265): a template's mob rows spawn fresh — no velocity/age/tame
  // state to restore, since a `WorldFileMob` is a declaration, not a snapshot
  // (`sim/save/worldfile.ts`). `loveTimer`/`breedCooldown`/`growTimer` at 0 and
  // `persistent: true` matches how a lesson's animals are meant to behave: ready
  // to breed/grow immediately, and never despawning for being unnamed/far-out.
  const mobRows: SavedEntity[] = file.mobs.map((m) => ({
    type: m.type, x: m.x, y: m.y, z: m.z, vx: 0, vy: 0, vz: 0, yaw: 0,
    health: m.health, age: 0, baby: m.baby, growTimer: 0, breedCooldown: 0, loveTimer: 0,
    persistent: true, ...(m.genome !== undefined ? { genome: m.genome } : {}),
  }));
  if (mobRows.length > 0) {
    const existing = await save.loadEntities(meta.id, HOST_DIM);
    await save.saveEntities(meta.id, HOST_DIM, [...existing, ...mobRows]);
  }

  return { meta, sections: file.sections.length, blockEntities: file.blockEntities.length, mobs: file.mobs.length };
}
